import { useNavigate } from 'react-router-dom'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Button, Drawer, Popconfirm, Select, Table, Tooltip } from 'antd'
// eslint-disable-next-line import/no-extraneous-dependencies
import type { ColumnsType } from 'antd/es/table'
// eslint-disable-next-line import/no-extraneous-dependencies
import Chart from 'react-apexcharts'
import { ToastContainer } from 'react-toastify'
import {
  ContainerTitle,
  DivTitle,
} from '../../../components/TitlePage/TitlePage'
import { Loading } from '../../../components/Loading/Loading'
import { colors } from '../../../theme'
import { useCentralResultadosDetalhe } from './useCentralResultadosDetalhe'
import {
  AVISO_CAUSALIDADE,
  AVISO_INDETERMINADO,
  AVISO_RECEITA,
  AVISO_RECEITA_RASTREADA,
  AVISO_VALOR_MEDIO,
  SEM_DADO,
  competenciasDisponiveis,
  formatCompetencia,
  formatData,
  formatDataHora,
  formatDecimal,
  formatMoeda,
  formatNumero,
  formatPercentual,
  motivoSemPositivacao,
  rotuloCobertura,
  rotuloReabertura,
  rotuloSemPositivacao,
} from '../centralResultados.helpers'
import {
  AcoesLinha,
  AvisoBloco,
  AvisoCausalidade,
  Badge,
  CardsGrid,
  Carimbo,
  ChartWrapper,
  Container,
  FilterBar,
  FilterField,
  HeaderLinha,
  LinhaBotoes,
  MetricCard,
  MetricFracao,
  MetricHint,
  MetricTitle,
  MetricValue,
  MetricValueSemDado,
  SectionTitle,
  TextoConfirmacao,
} from '../CentralResultados.style'

/** Card com o percentual e, embaixo, os absolutos que permitem refazer a conta. */
function CardTaxa({
  titulo,
  competencia,
}: {
  titulo: string
  competencia: CarteiraCompetenciaType
}) {
  const motivo = motivoSemPositivacao(competencia)
  return (
    <MetricCard>
      <MetricTitle>{titulo}</MetricTitle>
      {motivo ? (
        <>
          <MetricValueSemDado>{SEM_DADO}</MetricValueSemDado>
          <MetricHint>{motivo}</MetricHint>
        </>
      ) : (
        <>
          <MetricValue>
            {formatPercentual(competencia.positivacaoPercentual)}
          </MetricValue>
          <MetricFracao>
            {formatNumero(competencia.clientesAtivos)} ÷{' '}
            {formatNumero(competencia.clientesElegiveis)}
          </MetricFracao>
        </>
      )}
    </MetricCard>
  )
}

function CardValor({
  titulo,
  valor,
  dica,
}: {
  titulo: string
  valor: string
  dica?: string
}) {
  return (
    <MetricCard>
      <MetricTitle>
        {dica ? (
          <Tooltip title={dica}>
            <span>{titulo}</span>
          </Tooltip>
        ) : (
          titulo
        )}
      </MetricTitle>
      <MetricValue>{valor}</MetricValue>
    </MetricCard>
  )
}

export function CentralResultadosDetalhe() {
  const navigate = useNavigate()
  const {
    tenantId,
    detalhe,
    isLoading,
    erro,
    de,
    ate,
    onChangeDe,
    onChangeAte,
    drillCompetencia,
    vendedores,
    drillLoading,
    drillErro,
    abrirDrill,
    fecharDrill,
    isExportando,
    exportar,
    competenciaReabrindo,
    reabrir,
  } = useCentralResultadosDetalhe()

  // Calculado por render, e nao no escopo do modulo: uma aba aberta atravessando a virada do mes
  // ficaria sem a competencia nova, e discordaria de competenciaCorrente(), que e avaliada sempre.
  const opcoesCompetencia = competenciasDisponiveis(24).map(c => ({
    value: c,
    label: formatCompetencia(c),
  }))

  const serie = detalhe?.serie ?? []
  const ultima = serie.length > 0 ? serie[serie.length - 1] : null

  const chartSeries = [
    { name: 'Receita da carteira', data: serie.map(s => s.receitaTotal) },
    {
      name: 'Receita com rastro de previsão',
      data: serie.map(s => s.receitaRastreada),
    },
  ]

  const chartOptions: ApexCharts.ApexOptions = {
    // Sem `theme.mode` o ApexCharts mantem o default CLARO nos rotulos de eixo
    // (#373d3f), na legenda, na grade e no tooltip — trocar so o array `colors`
    // das series nao alcanca nada disso.
    theme: { mode: 'dark' },
    chart: {
      toolbar: { show: false },
      background: 'transparent',
      foreColor: colors.grey700,
    },
    grid: { borderColor: colors.grey300 },
    tooltip: { theme: 'dark' },
    colors: [colors.secondary, colors.lilas],
    stroke: { curve: 'smooth', width: 3 },
    xaxis: { categories: serie.map(s => formatCompetencia(s.competencia)) },
    yaxis: {
      labels: { formatter: valor => formatMoeda(valor) },
    },
    legend: { position: 'top' },
    dataLabels: { enabled: false },
  }

  const colunasSerie: ColumnsType<CarteiraCompetenciaType> = [
    {
      title: 'Competência',
      dataIndex: 'competencia',
      render: (_, r) => {
        const cobertura = rotuloCobertura(r)
        const reabertura = rotuloReabertura(r)
        return (
          <>
            <div>{formatCompetencia(r.competencia)}</div>
            {cobertura ? <Badge alerta={!r.fechada}>{cobertura}</Badge> : null}
            {/* A marca de reabertura é PERMANENTE e nunca é suprimida pela de cobertura: ela diz
                que alguém mexeu neste mês depois de fechado, e é a única informação da linha que
                não descreve o dado, e sim uma decisão humana sobre ele. */}
            {reabertura ? <Badge alerta>{reabertura}</Badge> : null}
          </>
        )
      },
    },
    {
      title: 'Receita',
      render: (_, r) => formatMoeda(r.receitaTotal),
    },
    {
      title: (
        <Tooltip title={AVISO_RECEITA_RASTREADA}>
          <span>Com rastro de previsão</span>
        </Tooltip>
      ),
      render: (_, r) => formatMoeda(r.receitaRastreada),
    },
    {
      title: 'Positivação',
      render: (_, r) => {
        const motivo = motivoSemPositivacao(r)
        if (motivo) {
          return (
            <Tooltip title={motivo}>
              <span>
                <Badge alerta={r.semMovimentoNaCompetencia}>
                  {rotuloSemPositivacao(r)}
                </Badge>
              </span>
            </Tooltip>
          )
        }
        return (
          <>
            <div>{formatPercentual(r.positivacaoPercentual)}</div>
            <MetricFracao>
              {formatNumero(r.clientesAtivos)} ÷{' '}
              {formatNumero(r.clientesElegiveis)}
            </MetricFracao>
          </>
        )
      },
    },
    {
      title: 'Base',
      render: (_, r) => formatNumero(r.clientesBase),
    },
    {
      title: (
        <Tooltip title={AVISO_INDETERMINADO}>
          <span>Indeterminados</span>
        </Tooltip>
      ),
      render: (_, r) => formatNumero(r.clientesIndeterminados),
    },
    {
      title: 'Recuperados',
      render: (_, r) => formatNumero(r.clientesRecuperados),
    },
    {
      title: (
        <Tooltip title="Inativos sem compra em nenhuma competência posterior já fechada. Em branco enquanto não houver mês posterior fechado para determinar.">
          <span>Perdidos</span>
        </Tooltip>
      ),
      render: (_, r) => formatNumero(r.clientesPerdidos),
    },
    {
      title: '',
      render: (_, r) => (
        <AcoesLinha>
          <Button size="small" onClick={() => abrirDrill(r.competencia)}>
            Por vendedor
          </Button>
          {/* Só competência FECHADA oferece a ação — a aberta é recalculada todo dia sozinha, e um
              botão "reabrir" nela seria um contador incrementado à toa. A API recusa de qualquer
              forma; esconder aqui evita oferecer o que não existe. */}
          {r.fechada ? (
            <Popconfirm
              title="Reabrir esta competência?"
              description={
                <TextoConfirmacao>
                  O mês será recalculado com o que o ERP entrega <b>hoje</b> e
                  fechado de novo. A competência fica marcada como reaberta{' '}
                  <b>para sempre</b>, com o seu nome e a data.
                  <br />
                  Se a janela do ERP já não cobrir o mês inteiro, a operação é
                  recusada — o histórico congelado é a única cópia que existe.
                </TextoConfirmacao>
              }
              okText="Reabrir"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => reabrir(r.competencia)}
            >
              <Button
                size="small"
                danger
                loading={competenciaReabrindo === r.competencia}
                disabled={
                  competenciaReabrindo !== null &&
                  competenciaReabrindo !== r.competencia
                }
              >
                Reabrir
              </Button>
            </Popconfirm>
          ) : null}
        </AcoesLinha>
      ),
    },
  ]

  const colunasVendedor: ColumnsType<CarteiraVendedorType> = [
    {
      title: 'Vendedor',
      render: (_, r) =>
        r.semVendedor ? (
          <Tooltip title="Vendas sem vendedor identificado na origem. A linha não pode ser escondida: sem ela a soma deixa de fechar com o card.">
            <span>
              <Badge>Sem vendedor</Badge>
            </span>
          </Tooltip>
        ) : (
          <>
            <div>{r.vendedorNome ?? SEM_DADO}</div>
            <MetricFracao>{r.vendedorCpfcnpj ?? ''}</MetricFracao>
          </>
        ),
      sorter: (a, b) =>
        (a.vendedorNome ?? '').localeCompare(b.vendedorNome ?? ''),
    },
    {
      title: 'Receita',
      render: (_, r) => formatMoeda(r.receitaTotal),
      sorter: (a, b) => a.receitaTotal - b.receitaTotal,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Positivação',
      render: (_, r) => {
        const motivo = motivoSemPositivacao(r)
        if (motivo) {
          return (
            <Tooltip title={motivo}>
              <span>
                <Badge alerta={r.semMovimentoNaCompetencia}>
                  {rotuloSemPositivacao(r)}
                </Badge>
              </span>
            </Tooltip>
          )
        }
        return (
          <>
            <div>{formatPercentual(r.positivacaoPercentual)}</div>
            <MetricFracao>
              {formatNumero(r.clientesAtivos)} ÷{' '}
              {formatNumero(r.clientesElegiveis)}
            </MetricFracao>
          </>
        )
      },
      sorter: (a, b) =>
        (a.positivacaoPercentual ?? -1) - (b.positivacaoPercentual ?? -1),
    },
    {
      title: 'Base',
      render: (_, r) => formatNumero(r.clientesBase),
      sorter: (a, b) => a.clientesBase - b.clientesBase,
    },
    {
      title: 'Indeterminados',
      render: (_, r) => formatNumero(r.clientesIndeterminados),
    },
    {
      title: (
        <Tooltip title={AVISO_VALOR_MEDIO}>
          <span>Valor médio</span>
        </Tooltip>
      ),
      render: (_, r) => formatMoeda(r.valorMedioPorClienteAtivo),
    },
  ]

  // Conferencia do rodape da gaveta: a soma das linhas FECHA com o card.
  //
  // ⚠️ A frase so pode ser impressa depois de COMPARAR. Antes ela era incondicional, e com a lista
  // vazia dizia "R$ 0,00 ... o card mostra R$ 180.000,00 - as duas fecham exatamente": um numero
  // que engana, com selo de auditoria. Se um dia divergirem, quem le tem de ver a divergencia, nao
  // uma afirmacao de que ela nao existe.
  const cardDoDrill =
    serie.find(s => s.competencia === drillCompetencia) ?? null
  // `+ (v.x ?? 0)` aqui NAO viola a regra do modulo: nao e exibir zero no lugar de "nao sei", e sim
  // somar uma coluna que o banco declara NOT NULL. Se algum dia vier nulo, o total nao bate e a
  // conferencia abaixo denuncia - que e exatamente o comportamento desejado.
  const somar = (extrair: (v: CarteiraVendedorType) => number | null): number =>
    vendedores.reduce((acc, v) => acc + (extrair(v) ?? 0), 0)
  const somaReceita = somar(v => v.receitaTotal)
  const somaAtivos = somar(v => v.clientesAtivos)
  const somaBase = somar(v => v.clientesBase)
  // Centavos: comparacao exata em ponto flutuante nao serve para dinheiro.
  const receitaFecha =
    cardDoDrill !== null &&
    Math.abs(somaReceita - cardDoDrill.receitaTotal) < 0.005
  const conferenciaFecha =
    cardDoDrill !== null &&
    vendedores.length > 0 &&
    receitaFecha &&
    somaAtivos === cardDoDrill.clientesAtivos &&
    somaBase === cardDoDrill.clientesBase

  if (isLoading) {
    return (
      <Container>
        <Loading contrast={false} />
      </Container>
    )
  }

  return (
    <Container>
      <ToastContainer theme="dark" />
      <ContainerTitle>
        <DivTitle color={colors.primaryText} size={24}>
          {detalhe?.nomeOrganizacao ?? tenantId}
        </DivTitle>
      </ContainerTitle>

      <AvisoCausalidade>{AVISO_CAUSALIDADE}</AvisoCausalidade>

      <HeaderLinha>
        <Carimbo>
          {tenantId} · Apurado em {formatDataHora(detalhe?.apuradoEm ?? null)}
        </Carimbo>
        <LinhaBotoes>
          <Button onClick={() => navigate('/adm-central-resultados')}>
            Voltar
          </Button>
          {/* Exporta a série E a decomposição por vendedor de cada competência do recorte: é o que
              torna o número conferível sem abrir a tela competência a competência. */}
          <Button
            onClick={exportar}
            loading={isExportando}
            disabled={isExportando || serie.length === 0}
          >
            {isExportando ? 'Gerando…' : 'Exportar XLSX'}
          </Button>
          {/* Ponte bidirecional: as duas telas respondem perguntas diferentes sobre a mesma conta
              — "usa o produto?" (Gestão de Tenants) x "a carteira melhorou?" (aqui). */}
          <Button
            onClick={() => navigate(`/adm-gestao-tenants/${tenantId}`)}
            type="link"
          >
            Ver uso do produto nesta conta
          </Button>
        </LinhaBotoes>
      </HeaderLinha>

      {erro ? <AvisoBloco erro>{erro}</AvisoBloco> : null}

      <SectionTitle>Contexto do número</SectionTitle>
      <CardsGrid>
        <MetricCard>
          <MetricTitle>Marco zero declarado</MetricTitle>
          {detalhe?.marcoZero ? (
            <>
              <MetricValue>{formatData(detalhe.marcoZero)}</MetricValue>
              <MetricHint>
                Informado por {detalhe.marcoZeroDeclaradoPor} em{' '}
                {formatDataHora(detalhe.marcoZeroDeclaradoEm)}
              </MetricHint>
              {detalhe.marcoZeroForaDaCobertura ? (
                <MetricHint>
                  <Badge alerta>
                    Anterior ao histórico — não é possível comparar &quot;desde
                    a implantação&quot;
                  </Badge>
                </MetricHint>
              ) : null}
            </>
          ) : (
            <>
              <MetricValueSemDado>{SEM_DADO}</MetricValueSemDado>
              <MetricHint>
                Não declarado. É uma declaração humana, feita no cadastro da
                organização — o sistema não tem como saber esta data.
              </MetricHint>
            </>
          )}
        </MetricCard>

        <MetricCard>
          <MetricTitle>Histórico próprio desde</MetricTitle>
          <MetricValue>
            {formatCompetencia(detalhe?.coberturaPropriaInicio ?? null)}
          </MetricValue>
          <MetricHint>
            A partir daqui o histórico é da Central e não depende mais da janela
            que o ERP entrega hoje.
          </MetricHint>
        </MetricCard>

        <MetricCard>
          <MetricTitle>Ciclo de compra da carteira</MetricTitle>
          <MetricValue>
            {detalhe?.intervaloMedianoCompras === null ||
            detalhe?.intervaloMedianoCompras === undefined
              ? SEM_DADO
              : `${formatDecimal(detalhe.intervaloMedianoCompras)} dias`}
          </MetricValue>
          <MetricHint>
            Intervalo mediano entre compras. Característica atual da conta — não
            muda ao trocar a competência.
          </MetricHint>
        </MetricCard>

        <MetricCard>
          <MetricTitle>Corte de inatividade</MetricTitle>
          <MetricValue>{detalhe?.diasInatividade ?? SEM_DADO} dias</MetricValue>
          <MetricHint>
            Igual para todas as contas, para os números serem comparáveis entre
            elas.
          </MetricHint>
        </MetricCard>
      </CardsGrid>

      <FilterBar>
        <FilterField>
          <span>De</span>
          <Select
            style={{ width: 180 }}
            value={de || undefined}
            onChange={onChangeDe}
            options={opcoesCompetencia}
            placeholder="Desde o início"
            allowClear
          />
        </FilterField>
        <FilterField>
          <span>Até</span>
          <Select
            style={{ width: 180 }}
            value={ate || undefined}
            onChange={onChangeAte}
            options={opcoesCompetencia}
            placeholder="Até a mais recente"
            allowClear
          />
        </FilterField>
      </FilterBar>

      {detalhe?.semBaseParaComparar ? (
        <AvisoBloco>
          Nenhuma competência congelada neste recorte — não há o que comparar.
        </AvisoBloco>
      ) : null}

      {ultima ? (
        <>
          <SectionTitle>
            Competência mais recente do recorte —{' '}
            {formatCompetencia(ultima.competencia)}
            {rotuloCobertura(ultima) ? (
              <>
                {' '}
                <Badge alerta={!ultima.fechada}>
                  {rotuloCobertura(ultima)}
                </Badge>
              </>
            ) : null}
          </SectionTitle>
          <CardsGrid>
            <CardTaxa titulo="Positivação" competencia={ultima} />
            <CardValor
              titulo="Receita da carteira"
              valor={formatMoeda(ultima.receitaTotal)}
              dica={AVISO_RECEITA}
            />
            <CardValor
              titulo="Com rastro de previsão"
              valor={formatMoeda(ultima.receitaRastreada)}
              dica={AVISO_RECEITA_RASTREADA}
            />
            <CardValor
              titulo="Valor médio por cliente ativo"
              valor={formatMoeda(ultima.valorMedioPorClienteAtivo)}
              dica={AVISO_VALOR_MEDIO}
            />
            <CardValor
              titulo="Itens distintos por cliente ativo"
              valor={formatDecimal(ultima.mixMedio)}
            />
            <CardValor
              titulo="Indeterminados"
              valor={formatNumero(ultima.clientesIndeterminados)}
              dica={AVISO_INDETERMINADO}
            />
          </CardsGrid>
          <LinhaBotoes>
            <Button onClick={() => abrirDrill(ultima.competencia)}>
              Abrir decomposição por vendedor
            </Button>
          </LinhaBotoes>
        </>
      ) : null}

      <SectionTitle>Evolução da receita</SectionTitle>
      <ChartWrapper>
        {serie.length > 0 ? (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={300}
          />
        ) : (
          // Sem esta guarda o Apex desenha um eixo 0-5% e a tela se le como "receita perto de zero".
          <MetricHint>
            {erro
              ? 'Não foi possível carregar a série.'
              : 'Sem competência congelada no recorte selecionado.'}
          </MetricHint>
        )}
      </ChartWrapper>

      <SectionTitle>Série por competência</SectionTitle>
      <Table
        rowKey="competencia"
        columns={colunasSerie}
        dataSource={serie}
        pagination={false}
        scroll={{ x: true }}
        locale={{
          // Com erro, "sem competência congelada" seria mentira: nao houve resposta para saber.
          emptyText: erro
            ? 'Não foi possível carregar a série.'
            : 'Sem competência congelada no recorte.',
        }}
      />

      <Drawer
        open={drillCompetencia !== null}
        onClose={fecharDrill}
        placement="right"
        width="min(1100px, 94vw)"
        title={`Por vendedor — ${formatCompetencia(drillCompetencia)}`}
      >
        {drillErro ? <AvisoBloco erro>{drillErro}</AvisoBloco> : null}
        {drillLoading ? (
          <Loading contrast={false} />
        ) : (
          <>
            <Table
              rowKey={(r, indice) =>
                `${r.vendedorCpfcnpj ?? ''}|${r.vendedorNome ?? ''}|${indice}`
              }
              columns={colunasVendedor}
              dataSource={vendedores}
              pagination={false}
              scroll={{ x: true }}
              locale={{
                emptyText: drillErro
                  ? 'Não foi possível carregar a decomposição.'
                  : 'Nenhuma linha de vendedor nesta competência.',
              }}
            />
            {cardDoDrill && !drillErro && vendedores.length > 0 ? (
              <AvisoBloco erro={!conferenciaFecha}>
                Soma das linhas: {formatMoeda(somaReceita)} ·{' '}
                {formatNumero(somaAtivos)} ativos · {formatNumero(somaBase)} na
                base. O card da competência mostra{' '}
                {formatMoeda(cardDoDrill.receitaTotal)} ·{' '}
                {formatNumero(cardDoDrill.clientesAtivos)} ativos ·{' '}
                {formatNumero(cardDoDrill.clientesBase)} na base
                {conferenciaFecha
                  ? ' — as duas leituras vêm da mesma fonte congelada e fecham exatamente.'
                  : ' — ⚠️ as duas leituras NÃO fecham. Não use estes números; registre a competência e a conta e acione o time de desenvolvimento.'}
              </AvisoBloco>
            ) : null}
          </>
        )}
      </Drawer>
    </Container>
  )
}
